import React, {useState} from 'react';
import './ClientAvatar.scss';

// Ocho colores para las iniciales de un cliente sin logo (D8 de la tanda iVirtual, 09/10). Hasta esa
// fecha todas las filas del Listado eran un cuadrado gris con dos letras, y con 26 clientes ninguno
// se reconocía de un vistazo.
//
// Cada uno lleva texto blanco a AA (≥ 4,5:1, medido: el peor es el cyan con 5,36 y lo vigila un
// test). Ninguno es del semáforo a propósito: un avatar rojo, verde o ámbar en una fila de
// «Requieren atención» se leería como severidad. Cuatro son tokens de la 2.1 (`--violet-ink`,
// `--status-dark-gray`, `--brand-dark-hover`, `--brand-dark`); el resto no tiene token y va en hex.
// El orden alterna tonos, así dos ids seguidos nunca caen en dos colores parecidos.
export const CLIENT_AVATAR_COLORS = [
    '#1d4ed8', // azul
    '#701251', // ciruela claro (--brand-dark-hover)
    '#0f766e', // verde azulado
    '#5546d6', // violeta (--violet-ink)
    '#475569', // pizarra (--status-dark-gray)
    '#7e22ce', // púrpura
    '#0e7490', // cyan
    '#440631', // ciruela (--brand-dark)
];

/**
 * Iniciales de un nombre: se ignora lo que va entre paréntesis («Cliente de Leads (complejo)») y se
 * toman las dos primeras palabras con contenido real — un separador suelto tipo «-» no cuenta
 * («Yuhmak - Ecommerce» → «YE», no «Y-»). Es la regla que tenían copiada el Listado y la ficha: un
 * cliente se tiene que ver igual en las dos pantallas.
 */
export function nameInitials(name) {
    const words = String(name || '').replace(/\(.*?\)/g, '').trim().split(/\s+/)
        .filter((word) => /[\p{L}\p{N}]/u.test(word));
    if (!words.length) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

/** El color de las iniciales de un cliente: su `id` módulo la paleta, así no cambia entre pantallas ni entre cargas. */
export function clientAvatarColor(id) {
    const number = Number(id);
    const index = Number.isFinite(number)
        ? Math.abs(Math.trunc(number)) % CLIENT_AVATAR_COLORS.length
        : 0;
    return CLIENT_AVATAR_COLORS[index];
}

/**
 * La imagen del avatar de un cliente: su ÍCONO (`icon_url`), que ve sólo el equipo de la agencia y no
 * aparece nunca en el portal (D11, ronda 2 de la tanda iVirtual, 09/10). Hasta esa ronda el avatar
 * mostraba `logo_url`, la marca del PORTAL, y por eso con iVirtual —que había cargado su propia marca
 * cliente por cliente— el Listado mostraba el logo de la agencia en todas las filas.
 *
 * Si la clave `icon_url` VIENE se usa tal cual, aunque sea `null` (el cliente no tiene ícono →
 * iniciales). Sólo si NO viene —backend anterior a la ronda 2, que no deploya junto con el frontend—
 * se cae a `logo_url`, que es lo que ese backend sabe mandar.
 */
export function clientIconUrl(client) {
    if (!client) return null;
    if (Object.prototype.hasOwnProperty.call(client, 'icon_url')) return client.icon_url || null;
    return client.logo_url || null;
}

/**
 * El avatar de un cliente: su ícono si lo tiene, y si no las iniciales en su color.
 *
 * La URL la elige quien lo monta con `clientIconUrl(client)`: es la del CLIENTE y nunca la de la
 * agencia (con `resolved_branding()`, que cae al de la agencia, todos los clientes tendrían el mismo
 * avatar). Si no hay, o la imagen no carga, van las iniciales: un logo roto es peor que dos letras.
 *
 * Decorativo para el lector de pantalla: el nombre del cliente está siempre al lado. El tamaño y el
 * radio los pone cada pantalla con `className`.
 */
export function ClientAvatar({id, name, logoUrl, className = ''}) {
    // La URL que falló, y no un booleano: si llega otra (se subió un logo nuevo), se vuelve a probar.
    const [failedUrl, setFailedUrl] = useState(null);
    const extraClass = className ? ` ${className}` : '';

    if (logoUrl && failedUrl !== logoUrl) {
        return (
            <span className={`client-avatar -logo${extraClass}`} aria-hidden="true" data-testid="client-avatar">
                <img src={logoUrl} alt="" loading="lazy" decoding="async"
                     onError={() => setFailedUrl(logoUrl)}/>
            </span>
        );
    }

    return (
        <span className={`client-avatar -initials${extraClass}`} aria-hidden="true" data-testid="client-avatar"
              style={{backgroundColor: clientAvatarColor(id)}}>
            {nameInitials(name)}
        </span>
    );
}

export default ClientAvatar;
